import { useState, useEffect, useCallback } from 'react'
import {
  Settings,
  ShoppingBag,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Save,
  Wifi,
  Database,
  Trash2,
  AlertTriangle,
  Link,
} from 'lucide-react'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { useSearchParams } from 'react-router-dom'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/config/firebase'
import { useAuth } from '@/hooks/useAuth'
import { useCompany } from '@/hooks/useCompany'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/useToast'
import { encryptText, type EncryptedPayload } from '@/lib/crypto'
import { FunctionsService } from '@/services/functions.service'
import {
  uploadCompanyLogo,
  uploadCompanyPdfLogo,
  uploadCompanyWatermark,
  uploadCompanyLoginLogo,
  uploadCompanyFavicon,
  fileToScaledPngDataUrl,
} from '@/services/storage.service'

interface ShopifyConfigDoc {
  data: string
  salt: string
  iv: string
  storeHint: string
  updatedAt: unknown
  updatedBy: string
}

interface ConfigStatus {
  configured: boolean
  storeHint: string
  updatedAt: Date | null
  updatedBy: string
}

function maskStore(url: string): string {
  try {
    const host = new URL(url.startsWith('http') ? url : `https://${url}`).hostname
    const parts = host.split('.')
    if (parts.length >= 2) return `${parts[0]}.myshopify.com`
    return host
  } catch {
    return url.substring(0, 20) + '…'
  }
}

export default function ConfigPage() {
  const { user } = useAuth()
  const { toast, toasts, dismiss } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const company = useCompany()

  // Company settings
  const [companyName, setCompanyName] = useState('')
  const [companyShort, setCompanyShort] = useState('')
  const [siteName, setSiteName] = useState('')
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [pdfLogoFile, setPdfLogoFile] = useState<File | null>(null)
  const [pdfLogoPreview, setPdfLogoPreview] = useState<string | null>(null)
  const [watermarkFile, setWatermarkFile] = useState<File | null>(null)
  const [watermarkPreview, setWatermarkPreview] = useState<string | null>(null)
  const [loginLogoFile, setLoginLogoFile] = useState<File | null>(null)
  const [loginLogoPreview, setLoginLogoPreview] = useState<string | null>(null)
  const [faviconFile, setFaviconFile] = useState<File | null>(null)
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null)
  const [savingCompany, setSavingCompany] = useState(false)

  useEffect(() => {
    setCompanyName(company.name)
    setCompanyShort(company.shortName)
    setSiteName(company.siteName ?? '')
    setLogoPreview(company.logoUrl)
    setPdfLogoPreview(company.pdfLogoData ?? company.pdfLogoUrl)
    setWatermarkPreview(company.watermarkUrl)
    setLoginLogoPreview(company.loginLogoUrl)
    setFaviconPreview(company.faviconUrl)
  }, [company])

  const [status, setStatus] = useState<ConfigStatus>({ configured: false, storeHint: '', updatedAt: null, updatedBy: '' })
  const [loadingStatus, setLoadingStatus] = useState(true)

  // OAuth app credentials (Client ID + Secret from Partners Dashboard)
  const [oauthClientId, setOauthClientId] = useState('')
  const [oauthClientSecret, setOauthClientSecret] = useState('')
  const [oauthShop, setOauthShop] = useState('')
  const [showOauthSecret, setShowOauthSecret] = useState(false)
  const [savingOauth, setSavingOauth] = useState(false)

  const [storeUrl, setStoreUrl] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [masterKey, setMasterKey] = useState('')
  const [showToken, setShowToken] = useState(false)
  const [showKey, setShowKey] = useState(false)

  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; shopName?: string; error?: string } | null>(null)

  const [clearingOrders, setClearingOrders] = useState(false)
  const [clearingProducts, setClearingProducts] = useState(false)

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true)
    try {
      const ref = doc(db, 'config', 'shopify')
      const snap = await getDoc(ref)
      if (snap.exists()) {
        const d = snap.data() as ShopifyConfigDoc
        setStatus({
          configured: true,
          storeHint: d.storeHint || '',
          updatedAt: (d.updatedAt as any)?.toDate?.() ?? null,
          updatedBy: d.updatedBy || '',
        })
      } else {
        setStatus({ configured: false, storeHint: '', updatedAt: null, updatedBy: '' })
      }
    } catch {
      // Firestore not available yet — keep defaults
    } finally {
      setLoadingStatus(false)
    }
  }, [])

  useEffect(() => { loadStatus() }, [loadStatus])

  // Handle OAuth callback result from URL params
  useEffect(() => {
    const oauthResult = searchParams.get('oauth')
    const shop = searchParams.get('shop')
    const msg = searchParams.get('msg')
    if (oauthResult === 'success') {
      toast({ title: '¡Tienda conectada!', description: `${shop} conectada correctamente vía OAuth` })
      loadStatus()
      setSearchParams({})
    } else if (oauthResult === 'error') {
      toast({ title: 'Error OAuth', description: msg ?? 'Error desconocido', variant: 'destructive' })
      setSearchParams({})
    }
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave() {
    if (!storeUrl.trim()) { toast({ title: 'Falta la URL de la tienda', variant: 'destructive' }); return }
    if (!accessToken.trim()) { toast({ title: 'Falta el Access Token', variant: 'destructive' }); return }
    if (masterKey.length < 8) { toast({ title: 'La llave maestra debe tener al menos 8 caracteres', variant: 'destructive' }); return }

    setSaving(true)
    setTestResult(null)
    try {
      const rawUrl = storeUrl.trim().replace(/\/$/, '')
      const clean = {
        storeUrl: rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`,
        accessToken: accessToken.trim(),
      }
      const payload: EncryptedPayload = await encryptText(JSON.stringify(clean), masterKey)
      const ref = doc(db, 'config', 'shopify')
      await setDoc(ref, {
        // Encrypted blob (for UI display and testShopifyConnection)
        ...payload,
        // Plain fields read by Cloud Functions (protected by Firestore auth rules)
        storeUrl: clean.storeUrl,
        accessToken: clean.accessToken,
        storeHint: maskStore(storeUrl.trim()),
        updatedAt: serverTimestamp(),
        updatedBy: user?.email ?? user?.uid ?? 'unknown',
      })
      await loadStatus()
      setStoreUrl('')
      setAccessToken('')
      setMasterKey('')
      toast({ title: 'Configuración guardada', description: 'Credenciales encriptadas y guardadas en Firestore' })
    } catch (err: any) {
      toast({ title: 'Error al guardar', description: err.message, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  async function handleTest() {
    if (!masterKey.trim()) { toast({ title: 'Ingresa la llave maestra para probar', variant: 'destructive' }); return }
    setTesting(true)
    setTestResult(null)
    try {
      const callable = httpsCallable<{ masterKey: string }, { success: boolean; shopName?: string; shopDomain?: string; error?: string }>(
        functions, 'testShopifyConnection'
      )
      const result = await callable({ masterKey })
      setTestResult(result.data)
    } catch (err: any) {
      const msg = err?.details?.message ?? err?.message ?? 'Error desconocido'
      setTestResult({ success: false, error: msg })
    } finally {
      setTesting(false)
    }
  }

  async function handleSaveOauthApp() {
    if (!oauthClientId.trim() || !oauthClientSecret.trim()) {
      toast({ title: 'Ingresa Client ID y Client Secret', variant: 'destructive' }); return
    }
    setSavingOauth(true)
    try {
      await setDoc(doc(db, 'config', 'shopifyApp'), {
        clientId: oauthClientId.trim(),
        clientSecret: oauthClientSecret.trim(),
        updatedAt: serverTimestamp(),
        updatedBy: user?.email ?? user?.uid ?? 'unknown',
      })
      toast({ title: 'Credenciales de app guardadas' })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    }
    setSavingOauth(false)
  }

  function handleConnectShopify() {
    if (!oauthClientId.trim()) { toast({ title: 'Guarda primero el Client ID', variant: 'destructive' }); return }
    const shop = oauthShop.trim().replace(/^https?:\/\//, '').replace(/\/$/, '')
    if (!shop) { toast({ title: 'Ingresa el dominio de la tienda', variant: 'destructive' }); return }
    const redirectUri = 'https://sale.wgkpr.com/shopify/callback'
    const scopes = 'read_orders,read_all_orders,read_products,read_inventory,read_customers'
    const state = crypto.randomUUID()
    sessionStorage.setItem('shopify_oauth_state', state)
    const url = `https://${shop}/admin/oauth/authorize?client_id=${oauthClientId.trim()}&scope=${scopes}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`
    window.location.href = url
  }

  async function handleSaveCompany() {
    if (!companyName.trim()) { toast({ title: 'El nombre no puede estar vacío', variant: 'destructive' }); return }
    setSavingCompany(true)
    try {
      let logoUrl = company.logoUrl
      let logoData = company.logoData
      if (logoFile) {
        logoUrl = await uploadCompanyLogo(logoFile)
        // Embed a small base64 copy so PDFs can use it without CORS
        logoData = await fileToScaledPngDataUrl(logoFile)
      }
      let pdfLogoUrl = company.pdfLogoUrl
      let pdfLogoData = company.pdfLogoData
      if (pdfLogoFile) {
        pdfLogoUrl = await uploadCompanyPdfLogo(pdfLogoFile)
        // Also embed a small base64 copy so the PDF can render it without CORS
        pdfLogoData = await fileToScaledPngDataUrl(pdfLogoFile)
      }
      let watermarkUrl = company.watermarkUrl
      if (watermarkFile) {
        watermarkUrl = await uploadCompanyWatermark(watermarkFile)
      }
      let loginLogoUrl = company.loginLogoUrl
      if (loginLogoFile) {
        loginLogoUrl = await uploadCompanyLoginLogo(loginLogoFile)
      }
      let faviconUrl = company.faviconUrl
      if (faviconFile) {
        faviconUrl = await uploadCompanyFavicon(faviconFile)
      }
      await setDoc(doc(db, 'config', 'company'), {
        name: companyName.trim(),
        shortName: companyShort.trim() || companyName.trim().split(' ')[0],
        siteName: siteName.trim() || null,
        logoUrl,
        logoData,
        pdfLogoUrl,
        pdfLogoData,
        watermarkUrl,
        loginLogoUrl,
        faviconUrl,
        updatedAt: serverTimestamp(),
        updatedBy: user?.email ?? user?.uid ?? 'unknown',
      })
      toast({ title: 'Configuración de empresa guardada' })
      setLogoFile(null)
      setPdfLogoFile(null)
      setWatermarkFile(null)
      setLoginLogoFile(null)
      setFaviconFile(null)
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    }
    setSavingCompany(false)
  }

  async function handleClearOrders() {
    setClearingOrders(true)
    const result = await FunctionsService.clearCache()
    if (result.data?.success) {
      toast({ title: 'Caché de órdenes limpiada', description: result.data.message })
    } else {
      toast({ title: 'Error', description: result.error, variant: 'destructive' })
    }
    setClearingOrders(false)
  }

  async function handleClearProducts() {
    setClearingProducts(true)
    const result = await FunctionsService.clearProductsCache()
    if (result.data?.success) {
      toast({ title: 'Caché de productos limpiada', description: result.data.message })
    } else {
      toast({ title: 'Error', description: result.error, variant: 'destructive' })
    }
    setClearingProducts(false)
  }

  const inputCls = 'bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-blue-500/50'

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map(t => (
          <div
            key={t.id}
            onClick={() => dismiss(t.id!)}
            className={`cursor-pointer rounded-lg px-4 py-3 shadow-lg text-sm max-w-xs ${
              t.variant === 'destructive' ? 'bg-red-900/90 text-red-100' : 'bg-gray-800 text-white'
            }`}
          >
            <p className="font-semibold">{t.title}</p>
            {t.description && <p className="text-white/70 mt-0.5">{t.description}</p>}
          </div>
        ))}
      </div>

      <div className="mx-auto w-full max-w-[1600px] px-4 py-8 pb-24 sm:px-6 lg:px-10">
        <Header />

        <div className="max-w-2xl space-y-6">

          {/* ── Company branding ── */}
          <section className="rounded-xl border border-white/10 bg-white/3 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-white/5">
              <Settings className="h-5 w-5 text-blue-400" />
              <h2 className="font-semibold text-white">Empresa</h2>
            </div>
            <div className="px-5 py-5 space-y-4">
              <div className="flex items-start gap-5">
                {/* Logo preview + upload */}
                <div className="shrink-0">
                  <div className="h-20 w-20 rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden bg-white/5 relative group cursor-pointer"
                    onClick={() => document.getElementById('logo-upload')?.click()}>
                    {logoPreview
                      ? <img src={logoPreview} alt="Logo" className="h-full w-full object-contain p-1" />
                      : <span className="text-white/25 text-xs text-center px-1">Logo</span>
                    }
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-medium">Cambiar</span>
                    </div>
                  </div>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0]
                      if (f) {
                        setLogoFile(f)
                        setLogoPreview(URL.createObjectURL(f))
                      }
                    }}
                  />
                  <p className="text-xs text-white/25 mt-1 text-center">PNG/JPG/SVG</p>
                </div>
                {/* Name fields */}
                <div className="flex-1 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-white/60 text-xs uppercase tracking-wide">Nombre completo</Label>
                    <Input value={companyName} onChange={e => setCompanyName(e.target.value)}
                      placeholder="New in the Box" className={inputCls} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-white/60 text-xs uppercase tracking-wide">Nombre corto (sidebar)</Label>
                    <Input value={companyShort} onChange={e => setCompanyShort(e.target.value)}
                      placeholder="NIBPR" className={inputCls} />
                  </div>
                </div>
              </div>

              {/* PDF logo */}
              <div className="flex items-start gap-5 border-t border-white/10 pt-4">
                <div className="shrink-0">
                  <div className="h-20 w-20 rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden bg-white relative group cursor-pointer"
                    onClick={() => document.getElementById('pdf-logo-upload')?.click()}>
                    {pdfLogoPreview
                      ? <img src={pdfLogoPreview} alt="Logo PDF" className="h-full w-full object-contain p-1" />
                      : <span className="text-gray-400 text-xs text-center px-1">Logo PDF</span>
                    }
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-medium">Cambiar</span>
                    </div>
                  </div>
                  <input
                    id="pdf-logo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0]
                      if (f) {
                        setPdfLogoFile(f)
                        setPdfLogoPreview(URL.createObjectURL(f))
                      }
                    }}
                  />
                  <p className="text-xs text-white/25 mt-1 text-center">PNG/JPG</p>
                </div>
                <div className="flex-1 space-y-1.5">
                  <Label className="text-white/60 text-xs uppercase tracking-wide">Logo para PDF</Label>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Se imprime en la esquina de los reportes PDF (metas y ventas). Usa una versión
                    pensada para <strong className="text-white/60">fondo blanco</strong> (idealmente el logo en
                    oscuro). No uses SVG aquí. Si no defines uno, se usa el logo de la empresa.
                  </p>
                </div>
              </div>

              {/* Login logo */}
              <div className="flex items-start gap-5 border-t border-white/10 pt-4">
                <div className="shrink-0">
                  <div className="h-20 w-20 rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden bg-gray-900 relative group cursor-pointer"
                    onClick={() => document.getElementById('login-logo-upload')?.click()}>
                    {loginLogoPreview
                      ? <img src={loginLogoPreview} alt="Logo login" className="h-full w-full object-contain p-1" />
                      : <span className="text-white/25 text-xs text-center px-1">Logo login</span>
                    }
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-medium">Cambiar</span>
                    </div>
                  </div>
                  <input
                    id="login-logo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0]
                      if (f) {
                        setLoginLogoFile(f)
                        setLoginLogoPreview(URL.createObjectURL(f))
                      }
                    }}
                  />
                  <p className="text-xs text-white/25 mt-1 text-center">PNG/SVG</p>
                </div>
                <div className="flex-1 space-y-1.5">
                  <Label className="text-white/60 text-xs uppercase tracking-wide">Logo de inicio de sesión</Label>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Se muestra en la pantalla de login sobre <strong className="text-white/60">fondo oscuro</strong>.
                    Si no defines uno, se usa el logo de la empresa.
                  </p>
                </div>
              </div>

              {/* Watermark logo */}
              <div className="flex items-start gap-5 border-t border-white/10 pt-4">
                <div className="shrink-0">
                  <div className="h-20 w-20 rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden bg-gray-900 relative group cursor-pointer"
                    onClick={() => document.getElementById('watermark-upload')?.click()}>
                    {watermarkPreview
                      ? <img src={watermarkPreview} alt="Marca de agua" className="h-full w-full object-contain p-1 opacity-70" />
                      : <span className="text-white/25 text-xs text-center px-1">Marca de agua</span>
                    }
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-medium">Cambiar</span>
                    </div>
                  </div>
                  <input
                    id="watermark-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0]
                      if (f) {
                        setWatermarkFile(f)
                        setWatermarkPreview(URL.createObjectURL(f))
                      }
                    }}
                  />
                  <p className="text-xs text-white/25 mt-1 text-center">PNG/SVG</p>
                </div>
                <div className="flex-1 space-y-1.5">
                  <Label className="text-white/60 text-xs uppercase tracking-wide">Marca de agua (fondo)</Label>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Aparece muy tenue detrás de varias pantallas (Stats, Resumen, Costos, Origen).
                    Preferiblemente un SVG o PNG monocromático. Si no defines uno, se usa el actual.
                  </p>
                </div>
              </div>

              {/* Favicon + site name */}
              <div className="flex items-start gap-5 border-t border-white/10 pt-4">
                <div className="shrink-0">
                  <div className="h-20 w-20 rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden bg-white/5 relative group cursor-pointer"
                    onClick={() => document.getElementById('favicon-upload')?.click()}>
                    {faviconPreview
                      ? <img src={faviconPreview} alt="Favicon" className="h-10 w-10 object-contain" />
                      : <span className="text-white/25 text-xs text-center px-1">Favicon</span>
                    }
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-medium">Cambiar</span>
                    </div>
                  </div>
                  <input
                    id="favicon-upload"
                    type="file"
                    accept="image/png,image/svg+xml,image/x-icon,image/vnd.microsoft.icon"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0]
                      if (f) {
                        setFaviconFile(f)
                        setFaviconPreview(URL.createObjectURL(f))
                      }
                    }}
                  />
                  <p className="text-xs text-white/25 mt-1 text-center">PNG/ICO</p>
                </div>
                <div className="flex-1 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-white/60 text-xs uppercase tracking-wide">Nombre del sitio (pestaña del navegador)</Label>
                    <Input value={siteName} onChange={e => setSiteName(e.target.value)}
                      placeholder="Shopify Sales Insights" className={inputCls} />
                    <p className="text-xs text-white/40 leading-relaxed">
                      Se usa como título de la pestaña del navegador y como encabezado en el login.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button size="sm" onClick={handleSaveCompany} disabled={savingCompany}
                  className="bg-blue-600 hover:bg-blue-700 text-white">
                  {savingCompany ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                  Guardar empresa
                </Button>
              </div>
            </div>
          </section>

          {/* ── OAuth Connect ── */}
          <section className="rounded-xl border border-blue-500/20 bg-blue-500/5 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-blue-500/15 bg-blue-500/8">
              <Link className="h-5 w-5 text-blue-400" />
              <div>
                <h2 className="font-semibold text-white">Conectar tienda vía OAuth</h2>
                <p className="text-xs text-white/40 mt-0.5">Usa las credenciales de tu app de Shopify Partners</p>
              </div>
            </div>
            <div className="px-5 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-white/70 text-xs uppercase tracking-wide">Client ID</label>
                  <Input
                    value={oauthClientId}
                    onChange={e => setOauthClientId(e.target.value)}
                    placeholder="De la sección API credentials"
                    className={inputCls}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-white/70 text-xs uppercase tracking-wide">Client Secret</label>
                  <div className="relative">
                    <Input
                      value={oauthClientSecret}
                      onChange={e => setOauthClientSecret(e.target.value)}
                      type={showOauthSecret ? 'text' : 'password'}
                      placeholder="Client Secret"
                      className={`${inputCls} pr-10`}
                    />
                    <button type="button" onClick={() => setShowOauthSecret(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                      {showOauthSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-white/70 text-xs uppercase tracking-wide">Dominio de la tienda</label>
                <Input
                  value={oauthShop}
                  onChange={e => setOauthShop(e.target.value)}
                  placeholder="tu-tienda.myshopify.com"
                  className={inputCls}
                />
              </div>
              <div className="flex gap-3 pt-1">
                <Button size="sm" variant="outline"
                  onClick={handleSaveOauthApp} disabled={savingOauth}
                  className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                  {savingOauth ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                  Guardar credenciales
                </Button>
                <Button size="sm"
                  onClick={handleConnectShopify}
                  className="bg-blue-600 hover:bg-blue-700 text-white ml-auto">
                  <Link className="h-3.5 w-3.5 mr-1.5" />
                  Conectar tienda →
                </Button>
              </div>
              <p className="text-xs text-white/30">
                Shopify Partners → NIBPR Analytics → API credentials → copia Client ID y Client Secret
              </p>
            </div>
          </section>

          {/* ── Shopify ── */}
          <section className="rounded-xl border border-white/10 bg-white/3 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/5">
              <div className="flex items-center gap-3">
                <ShoppingBag className="h-5 w-5 text-green-400" />
                <h2 className="font-semibold text-white">Integración Shopify</h2>
              </div>
              {loadingStatus ? (
                <span className="text-xs text-white/30">Cargando…</span>
              ) : status.configured ? (
                <span className="inline-flex items-center gap-1.5 text-xs bg-green-500/15 text-green-400 border border-green-500/25 rounded-full px-3 py-1">
                  <CheckCircle2 className="h-3 w-3" /> Configurado
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs bg-amber-500/15 text-amber-400 border border-amber-500/25 rounded-full px-3 py-1">
                  <AlertTriangle className="h-3 w-3" /> Sin configurar
                </span>
              )}
            </div>

            <div className="px-5 py-5 space-y-5">
              {/* Current config summary */}
              {status.configured && (
                <div className="rounded-lg bg-white/5 border border-white/8 px-4 py-3 text-sm space-y-1">
                  <div className="flex items-center gap-2 text-white/70">
                    <span className="text-white/40 w-24 shrink-0">Tienda</span>
                    <span className="font-mono text-green-300">{status.storeHint}</span>
                  </div>
                  {status.updatedAt && (
                    <div className="flex items-center gap-2 text-white/70">
                      <span className="text-white/40 w-24 shrink-0">Actualizado</span>
                      <span>{status.updatedAt.toLocaleString()}</span>
                    </div>
                  )}
                  {status.updatedBy && (
                    <div className="flex items-center gap-2 text-white/70">
                      <span className="text-white/40 w-24 shrink-0">Por</span>
                      <span>{status.updatedBy}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Form */}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-white/70 text-xs uppercase tracking-wide">
                    Shopify Store URL
                  </Label>
                  <Input
                    value={storeUrl}
                    onChange={e => setStoreUrl(e.target.value)}
                    placeholder="https://tu-tienda.myshopify.com"
                    className={inputCls}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-white/70 text-xs uppercase tracking-wide">
                    Admin API Access Token
                  </Label>
                  <div className="relative">
                    <Input
                      value={accessToken}
                      onChange={e => setAccessToken(e.target.value)}
                      type={showToken ? 'text' : 'password'}
                      placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxx"
                      className={`${inputCls} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowToken(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    >
                      {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-white/70 text-xs uppercase tracking-wide">
                    Llave Maestra
                  </Label>
                  <div className="relative">
                    <Input
                      value={masterKey}
                      onChange={e => setMasterKey(e.target.value)}
                      type={showKey ? 'text' : 'password'}
                      placeholder="Min. 8 caracteres — encripta las credenciales"
                      className={`${inputCls} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowKey(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    >
                      {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-white/30">
                    Recuerda esta llave — se usa para encriptar/desencriptar. No se almacena en ningún lado.
                  </p>
                </div>
              </div>

              {/* Test result */}
              {testResult && (
                <div className={`rounded-lg border px-4 py-3 text-sm flex items-start gap-3 ${
                  testResult.success
                    ? 'bg-green-500/10 border-green-500/25 text-green-300'
                    : 'bg-red-500/10 border-red-500/25 text-red-300'
                }`}>
                  {testResult.success
                    ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                    : <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  }
                  <div>
                    {testResult.success
                      ? <><strong>{testResult.shopName}</strong> — conexión exitosa</>
                      : testResult.error
                    }
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTest}
                  disabled={testing || !status.configured}
                  className="bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-40"
                >
                  {testing
                    ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Wifi className="h-3.5 w-3.5 mr-1.5" />
                  }
                  Probar conexión
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white ml-auto"
                >
                  {saving
                    ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Save className="h-3.5 w-3.5 mr-1.5" />
                  }
                  {status.configured ? 'Actualizar' : 'Guardar'}
                </Button>
              </div>
            </div>
          </section>

          {/* ── Firebase info ── */}
          <section className="rounded-xl border border-white/10 bg-white/3 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-white/5">
              <Settings className="h-5 w-5 text-orange-400" />
              <h2 className="font-semibold text-white">Firebase</h2>
            </div>
            <div className="px-5 py-4 space-y-2 text-sm">
              {[
                ['Proyecto', 'salewgknib'],
                ['Dominio', 'sale.wgkpr.com'],
                ['Hosting', 'salewgknib.web.app'],
                ['Firestore', 'Activo'],
                ['Functions', 'Región: us-central1'],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center gap-3">
                  <span className="text-white/40 w-24 shrink-0">{label}</span>
                  <span className="text-white/80 font-mono text-xs">{value}</span>
                </div>
              ))}
            </div>
          </section>

          {/* ── Cache ── */}
          <section className="rounded-xl border border-white/10 bg-white/3 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-white/5">
              <Database className="h-5 w-5 text-purple-400" />
              <h2 className="font-semibold text-white">Gestión de Caché</h2>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-white/40">
                Borra el caché cuando cambies la tienda o necesites datos frescos desde Shopify.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearOrders}
                  disabled={clearingOrders}
                  className="bg-white/5 border-white/10 text-white hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-300"
                >
                  {clearingOrders
                    ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  }
                  Limpiar caché de órdenes
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearProducts}
                  disabled={clearingProducts}
                  className="bg-white/5 border-white/10 text-white hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-300"
                >
                  {clearingProducts
                    ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  }
                  Limpiar caché de productos
                </Button>
              </div>
            </div>
          </section>

        </div>
      </div>

    </div>
  )
}
